import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import {
  mapAnalyticsSummary,
  mapRequestsByCity,
  mapRequestsBySlot,
  mapRequestsTimeseriesPoint,
  mapSignupsTimeseriesPoint,
  mapSupportTimeseriesPoint,
  type AnalyticsGranularity,
  type AnalyticsSummary,
  type RequestsByCity,
  type RequestsBySlot,
  type RequestsTimeseriesPoint,
  type SignupsTimeseriesPoint,
  type SupportTimeseriesPoint,
} from '../model/analytics';

/** Drives every time-bucketed `analytics_*_timeseries` RPC. */
export interface AnalyticsRange {
  readonly granularity: AnalyticsGranularity;
  /** ISO date (YYYY-MM-DD), inclusive. */
  readonly from: string;
  /** ISO date (YYYY-MM-DD), inclusive. */
  readonly to: string;
}

/**
 * Phase 9 "Insights" dashboard repository. Every call hits one of the
 * admin-gated, SQL-aggregating RPCs in
 * `supabase/migrations/20260722000000_phase9_analytics.sql` — the DB re-checks
 * `is_admin` itself, so a non-admin request fails with `not-authorized` even
 * if this ever got called from outside `AdminGate`.
 */
export const analyticsRepository = {
  async summary(): Promise<Result<AnalyticsSummary>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_summary');
      if (error) throw fromPostgrest(error);
      const row = ((data ?? []) as unknown[])[0];
      if (!row) raise('server', 'server');
      return mapAnalyticsSummary(row);
    });
  },

  async requestsTimeseries(
    range: AnalyticsRange,
  ): Promise<Result<readonly RequestsTimeseriesPoint[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_requests_timeseries', {
        p_granularity: range.granularity,
        p_from: range.from,
        p_to: range.to,
      });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapRequestsTimeseriesPoint);
    });
  },

  async requestsByCity(): Promise<Result<readonly RequestsByCity[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_requests_by_city');
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapRequestsByCity);
    });
  },

  async requestsBySlot(): Promise<Result<readonly RequestsBySlot[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_requests_by_slot');
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapRequestsBySlot);
    });
  },

  async signupsTimeseries(
    range: AnalyticsRange,
  ): Promise<Result<readonly SignupsTimeseriesPoint[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_signups_timeseries', {
        p_granularity: range.granularity,
        p_from: range.from,
        p_to: range.to,
      });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapSignupsTimeseriesPoint);
    });
  },

  async supportTimeseries(
    range: AnalyticsRange,
  ): Promise<Result<readonly SupportTimeseriesPoint[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('analytics_support_timeseries', {
        p_granularity: range.granularity,
        p_from: range.from,
        p_to: range.to,
      });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapSupportTimeseriesPoint);
    });
  },
};
